
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 */

import { useMemo } from 'react';
import useSearch from './useSearch';

// Normalize helper: supports string | string[] | comma-separated string
function normalize(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (typeof value === 'string') {
    const parts = value.includes(',') ? value.split(',') : [value];

    return parts.map((s) => (s || '').trim()).filter(Boolean);
  }

  return [];
}

/**
 * From Tabular column definitions, derive which row property names participate in search.
 */
function getActiveSearchFieldKeys(columns) {
  const list = Array.isArray(columns) ? columns : [];
  const firstColumn = list[0] ?? null;
  const primaryEnabled = !!firstColumn?.primarySearch;

  let primaryKeys = normalize(firstColumn?.primarySearchFields);
  if (!primaryKeys.length && primaryEnabled) {
    primaryKeys = normalize(firstColumn?.field);
  }

  // return primary keys if primary is enabled and primary keys are present
  if (primaryEnabled && primaryKeys.length > 0) {
    return primaryKeys;
  }

  const secondaryKeys = list
    .filter((col) => col?.secondarySearch)
    .flatMap((col) => normalize(col.secondarySearchFields || col.field))
    .filter(Boolean);

  // return secondary keys if secondary is enabled and secondary keys are present
  return secondaryKeys.length > 0 ? secondaryKeys : [];
}

/**
 * useAdvancedSearch
 * Wraps useSearch and decides search fields based on primary/secondary flags.As of now supporting primary and secondary flags, this can be modified to support
 * more
 *
 * @param {Array<any>} data
 * @param {Object} opts
 * @param {Array<{ title: string, field?: string|string[], primarySearch?: boolean, secondarySearch?: boolean }>} opts.columns
 * @param {string[]|undefined} opts.defaultPrimaryFields - fallback when primary is true and no fields provided (default ['question','topic'])
 * @param {boolean} opts.useSecondary - when true, prefer secondary fields if provided
 * @param {string[]} opts.secondaryFields - explicit secondary fields override
 * @param {string[]} opts.primaryFields - explicit primary fields override
 */
export default function useAdvancedSearch(data, opts = {}) {
  const {
    columns = []
  } = opts;

  // Memoized calculation of active fields.
  const activeFields = useMemo(
    () => getActiveSearchFieldKeys(columns),
    [columns]
  );

  const base = useSearch(data, { searchKeys: activeFields, strictKeys: true });


  const filteredData = (activeFields && activeFields.length > 0) ? base.filteredData : data || [];

  return { ...base, filteredData };
}

